// ============================================================================
// 자동화 규칙 API 서비스 (CHG-047)
// ============================================================================

import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';

export interface AutomationRule {
  ruleId: string;
  projectId: string;
  name: string;
  description: string | null;
  templateType: string | null;
  triggerEvent: string;
  conditions: { field: string; operator: string; value?: unknown }[];
  actions: { type: string; params: Record<string, unknown> }[];
  priority: number;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: { id: string; name: string };
  logs: { logId: string; status: string; executedAt: string; triggerEvent: string }[];
}

export interface AutomationLog {
  logId: string;
  ruleId: string;
  issueId: string | null;
  prId: string | null;
  triggerEvent: string;
  conditionsMet: boolean;
  actionsResult: { type: string; success: boolean; message: string }[];
  status: string;
  errorMessage: string | null;
  executedAt: string;
  rule: { ruleId: string; name: string; triggerEvent: string };
}

export interface TemplateInfo {
  templateType: string;
  name: string;
  description: string;
  ruleCount: number;
}

export interface CreateRuleData {
  name: string;
  description?: string;
  triggerEvent: string;
  conditions: { field: string; operator: string; value?: unknown }[];
  actions: { type: string; params: Record<string, unknown> }[];
  priority?: number;
}

export async function fetchRules(projectId: string): Promise<AutomationRule[]> {
  const res = await api.get<ApiResponse<AutomationRule[]>>(`/projects/${projectId}/automation/rules`);
  return res.data.data!;
}

export async function createRule(projectId: string, data: CreateRuleData) {
  const res = await api.post<{ success: boolean; data: AutomationRule; warnings: string[] }>(`/projects/${projectId}/automation/rules`, data);
  return res.data;
}

export async function updateRule(projectId: string, ruleId: string, data: Partial<CreateRuleData> & { isEnabled?: boolean }) {
  const res = await api.put<{ success: boolean; data: AutomationRule; warnings: string[] }>(`/projects/${projectId}/automation/rules/${ruleId}`, data);
  return res.data;
}

export async function deleteRule(projectId: string, ruleId: string) {
  await api.delete(`/projects/${projectId}/automation/rules/${ruleId}`);
}

export async function toggleRule(projectId: string, ruleId: string) {
  const res = await api.post<ApiResponse<{ ruleId: string; isEnabled: boolean }>>(`/projects/${projectId}/automation/rules/${ruleId}/toggle`);
  return res.data.data!;
}

export async function fetchLogs(projectId: string, params?: Record<string, string>) {
  const res = await api.get<{ success: boolean; data: AutomationLog[]; total: number; page: number; limit: number }>(`/projects/${projectId}/automation/logs`, { params });
  return res.data;
}

export async function fetchTemplates(projectId: string): Promise<TemplateInfo[]> {
  const res = await api.get<ApiResponse<TemplateInfo[]>>(`/projects/${projectId}/automation/templates`);
  return res.data.data!;
}

export async function applyTemplate(projectId: string, templateType: string) {
  const res = await api.post<{ success: boolean; data: { matched: number; unmatched: number; totalRules: number }; warnings: string[] }>(`/projects/${projectId}/automation/templates`, { templateType });
  return res.data;
}
